import { z } from 'zod';

/**
 * The person's own account (launch-plan step 2). Everything here belongs to `users.id` and to
 * nothing else: no programme, no roster, no earned record. The record view and the access list are
 * step 3, and verified addresses and linked identities are step 4, so this contract reports what
 * is true today and claims nothing it cannot prove.
 */

/** The same bound the credential name has carried since the first release. */
export const ACCOUNT_NAME_MAX = 120;

export const EmailVerification = z.enum(['legacy_unknown', 'email_link', 'provider', 'admin_recovery']);

/** One address on the account. In this step every row is the backfilled `legacy_unknown` primary. */
export const AccountEmail = z.object({
  id: z.string(),
  email: z.string(),
  verification: EmailVerification,
  verified_at: z.string().nullable(),
  is_primary: z.boolean(),
});
export type AccountEmail = z.infer<typeof AccountEmail>;

/**
 * A way in. Email links work. A provider appears only once configuration selects one, and then
 * only to say it is not yet available; the seam never enables it in this release.
 */
export const AccountSignInMethod = z.object({
  method: z.enum(['email_link', 'provider']),
  issuer: z.string().nullable(),
  enabled: z.boolean(),
  detail: z.string(),
});
export type AccountSignInMethod = z.infer<typeof AccountSignInMethod>;

export const AccountRecord = z.object({
  account: z.object({
    user_id: z.string(),
    email: z.string(),
    name: z.string().nullable(),
    photo_url: z.string().nullable(),
    is_superadmin: z.boolean(),
    /**
     * The optimistic-concurrency token: a fingerprint of the fields this surface may change, not
     * the row's `updated_at`, which the hourly last-seen write also moves.
     */
    revision: z.string(),
    authenticated_at: z.string().nullable(),
    auth_method: z.enum(['email_link', 'legacy_session', 'provider']).nullable(),
  }),
  emails: z.array(AccountEmail),
  methods: z.array(AccountSignInMethod),
  recovery: z.object({
    verified_addresses: z.number().int().nonnegative(),
    /** Adding or changing an address yourself arrives in step 4. */
    self_service: z.boolean(),
    /**
     * Reviewed recovery by a named administrator, Brendon's choice of 2026-09-21. False until that
     * administrator and the independent-evidence procedure are agreed, which the plan puts before
     * step 4: the contract does not report a path nobody can yet take.
     */
    administrator_path: z.boolean(),
  }),
});
export type AccountRecord = z.infer<typeof AccountRecord>;

/** Strict: an unknown field is refused, never silently dropped. */
export const AccountPatch = z.strictObject({
  /** The person whose form was opened, checked against the session at save time. */
  user_id: z.string().min(1),
  name: z.string().trim().min(1).max(ACCOUNT_NAME_MAX),
  /** The `revision` the client last read. A mismatch is a 409, never a silent overwrite. */
  revision: z.string().min(1).max(64),
});
export type AccountPatch = z.infer<typeof AccountPatch>;

/**
 * Database-to-response projections. The canon validates what leaves the database as strictly as
 * what arrives from a client: a column that changes shape fails here, not in a browser.
 */
export const AccountUserProjection = z
  .object({
    id: z.string().min(1),
    email: z.string().min(3).max(254),
    name: z.string().max(ACCOUNT_NAME_MAX).nullable(),
    is_superadmin: z.boolean(),
  })
  .strict();

export const AccountEmailProjection = z
  .object({
    id: z.string().min(1),
    email: z.string().min(3).max(254),
    verification: EmailVerification,
    verified_at: z.string().nullable(),
    is_primary: z.boolean(),
  })
  .strict();
